import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { t } from "../lib/i18n";
import { qk } from "./keys";
import type {
  ActivityReport,
  ReportFilters,
  ReportSearchFilters,
  ReportSectionInput,
  SaveWeekResult,
  WeekContext,
} from "./types";

/** `week` = "2026-W40", or "default" to let the backend pick the previous week. */
export function useWeekContext(week: string, enabled = true) {
  return useQuery({
    queryKey: qk.weekContext(week),
    queryFn: ({ signal }) => api<WeekContext>(`/api/reports/me/${encodeURIComponent(week)}`, { signal }),
    enabled,
  });
}

export function useSaveWeek() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ week, submit, sections }: { week: string; submit: boolean; sections: ReportSectionInput[] }) =>
      api<SaveWeekResult>(`/api/reports/me/${encodeURIComponent(week)}`, {
        method: "PUT",
        body: { submit, sections },
      }),
    meta: { errorTitle: t("Report not saved") },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.reports });
      void client.invalidateQueries({ queryKey: qk.me });
      void client.invalidateQueries({ queryKey: ["admin", "compliance"] });
    },
  });
}

export function useReports(filters: ReportFilters, enabled = true) {
  return useQuery({
    queryKey: qk.reportList(filters),
    queryFn: ({ signal }) =>
      api<ActivityReport[]>("/api/reports", {
        signal,
        query: {
          project: filters.project,
          person_id: filters.person_id,
          stream_id: filters.stream_id,
          week: filters.week,
          week_from: filters.week_from,
          week_to: filters.week_to,
          mine: filters.mine ? true : undefined,
          limit: filters.limit,
        },
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useReportSearch(filters: ReportSearchFilters, enabled = true) {
  return useQuery({
    queryKey: qk.reportSearch(filters),
    queryFn: ({ signal }) =>
      api<ActivityReport[]>("/api/reports/search", {
        signal,
        query: {
          q: filters.q,
          project: filters.project,
          person_id: filters.person_id,
          week_from: filters.week_from,
          week_to: filters.week_to,
        },
      }),
    enabled: enabled && filters.q.trim() !== "",
    placeholderData: keepPreviousData,
  });
}
